import { StrokedText } from "@/components/ui/text/stroked-text";
import { useEffect, useRef, useState } from "react";

export const ItemMiniItem = ({
  itemName,
  amount,
  onClick,
  onLongPress,
  selected,
}: {
  itemName: string;
  amount: number;
  onClick: () => void;
  onLongPress?: () => void;
  selected: boolean;
}) => {
  const [isPressing, setIsPressing] = useState(false);
  const timerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // cancel context menu on long-press (mobile)
  useEffect(() => {
    return () => clearTimer();
  }, []);

  const startPress = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onLongPress) return; // still allow normal click path
    setIsPressing(true);
    longPressFiredRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };

    timerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      setIsPressing(false);
      onLongPress?.();
    }, 500);
  };

  const endPress = () => {
    setIsPressing(false);
    clearTimer();
  };

  const cancelIfMovedTooMuch = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPressing || !startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    const tolerance = 10; // px
    if (dx > tolerance || dy > tolerance) {
      // treat as cancel
      endPress();
    }
  };

  const handleClick = () => {
    // If long-press fired, swallow click
    if (longPressFiredRef.current) return;
    onClick?.();
  };

  // Split by `-` and capitalize the first letter of each word
  const parsedItemName = itemName
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <div
      className={`flex flex-col cursor-pointer transition active:scale-95  rounded-sm   items-center justify-center relative pt-2 h-24 xs:h-26 ${
        selected
          ? "bg-pk-beige-1/60 hover:bg-pk-beige-1/40"
          : "bg-pk-gray-2 hover:bg-pk-beige-1/60"
      }`}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onPointerLeave={endPress}
      onPointerMove={cancelIfMovedTooMuch}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img
        src={`/assets/items/${itemName}.png`}
        alt={itemName}
        className="w-14 h-14 object-contain image-rendering-pixelated absolute top-0"
      />
      <div
        className="leading-4 text-center text-[10px] xs:text-xs text-pk-blue font-semibold mt-auto mb-1 px-1 z-10"
        style={{
          textShadow: "2px -2px 0 rgba(0,0,0,0.85)",
        }}
      >
        {parsedItemName}
      </div>
      <div className="text-lg leading-tight w-full flex justify-end px-1 mb-1">
        <div className="bg-pk-night-2 rounded-b-sm pt-1 ml-auto h-full flex items-center justify-center pr-1 pl-4">
          <StrokedText fontSize={12} strokeWidth={6} letterSpacing={1}>
            {amount.toString()}
          </StrokedText>
        </div>
      </div>
    </div>
  );
};

export const ItemLoadingSkeleton = () => {
  return (
    <div className="flex flex-col rounded-sm animate-pulse items-center justify-center relative h-20 bg-pk-gray-2/50"></div>
  );
};
